import {
  componentHasAdoptedShadowStyle,
  componentHasShadowRoot,
  componentHasShadowStyle,
  browserSupportsShadowDom
} from "../utils/shadow-dom-helper";

/**
 * Sets a CSS variable for the given component.
 * If the current browser supports ShadowDOM, the variable will be appended to the <style> tag of the shadowRoot.
 * If it does not (IE, Edge < 80, unit/snapshot tests), the variable will be set as inline style on the hostElement.
 *
 * @param hostElement The component in whose context the CSS variable should be set
 * @param name The name of the CSS variable, e.g. "--myvar"
 * @param value The value of the CSS variable to set, e.g. "var(--eon-white)"
 */
export function setCssVariable(hostElement: HTMLElement, name: string, value: string): void {
  if (componentHasAdoptedShadowStyle(hostElement)) {
    // Update adopted shadow styles.
    const variableStyleSheet =
      hostElement.shadowRoot.adoptedStyleSheets[
        hostElement.shadowRoot.adoptedStyleSheets.length - 2 // adoptedStyleSheets.length - 2 is index of the style sheet for the variables set by setCssVariable() function
      ];
    const variableStyleSheetsLength = variableStyleSheet?.cssRules.length;

    variableStyleSheet?.insertRule(`:host { ${name}: ${value} }`, variableStyleSheetsLength);
  } else if (componentHasShadowStyle(hostElement)) {
    // Append the CSS variable declaration to the <style> tag of the shadowRoot
    (hostElement.shadowRoot.styleSheets[0] as any).addRule(`:host`, `${name}: ${value}`);
  } else if (!componentHasShadowRoot(hostElement) || !browserSupportsShadowDom()) {
    // Add the CSS variable declaration as inline style to the hostElement
    hostElement.style.setProperty(`${name}`, `${value}`);
  } // Else: component might be shadowed in a modern browser but the stylesheet isn't ready yet. Happens frequently in FF/Chrome.
}

export function createSchemeStyleScheet(activeScheme: any): any {
  const schemeStyleSheet: any = new CSSStyleSheet();
  for (let [key, value] of Object.entries(activeScheme)) {
    // This check allows also setting non color variable values to a component scheme
    if (typeof value === "string" && value.startsWith("eon-")) {
      schemeStyleSheet.insertRule(`:host { --${key}: var(--${value}) }`);
    } else {
      schemeStyleSheet.insertRule(`:host { --${key}: ${value} }`);
    }
  }

  return schemeStyleSheet;
}

/**
 * Returns the value of the given CSS variable defined as inline style on the hostElement (IE/Edge/tests) or within its shadowRoot.
 *
 * @param hostElement The component in whose context the CSS variable is set
 * @param name The name of the CSS variable, e.g. "--myvar"
 */
export function getCssVariable(hostElement: HTMLElement, name: string): string {
  const style = window.getComputedStyle(hostElement);
  if (style != null) return style.getPropertyValue(name);
  return null;
}
